import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import Shepherd from 'shepherd.js';
import { TourInstance, TourStep } from '../models/tour.models';

const tourDefaultOptions = {
  useModalOverlay: true,
  defaultStepOptions: {
    modalOverlayOpeningRadius: 4,
    modalOverlayOpeningPadding: 4,
    canClickTarget: false,
    cancelIcon: { enabled: true },
    scrollTo: { behavior: 'smooth', block: 'center' },
  },
} as any;

@Injectable({ providedIn: 'root' })
export class TourService {
  translations = this.translate.instant(`tour`);
  readonly subject = new ReplaySubject<{ tourEnabled: boolean; source?: 'shepherd' }>(1);
  readonly tourPreStart = new BehaviorSubject<boolean>(false);
  tourPreStart$ = this.tourPreStart.asObservable().pipe(filter((d) => !!d));

  steps = [];

  tour: Shepherd.Tour;

  enableTour(a) {
    if (this.tour && this.steps.length) return;

    this.init(a);
    this.subject.next({ tourEnabled: true });
  }
  renderTour(instance: TourInstance, steps: TourStep[]): Shepherd.Tour {
    const translations = this.translations[instance];
    steps = steps.map((step) => ({
      ...step,
      ...(translations[step.key] || {}),
    }));
    this.enableTour(steps);
    this.init(steps);

    return this.tour;
  }

  disableTour(complete = true) {
    this.steps = [];
    this.tourPreStart.next(false);
    if (this.tour) {
      try {
        this.tour.complete();
      } catch {}
      this.tour.on('complete', null);
      this.tour.on('cancel', null);
      this.tour.on('show', null);
      this.tour = null;
    }

    if (complete) {
      this.subject.next({ tourEnabled: false });
    }
  }
  init(steps) {
    this.tour = new Shepherd.Tour(tourDefaultOptions);
    this.steps = this.generateSteps(steps);
    this.tour.addSteps(this.steps);
  }

  start() {
    if (this.steps.length) {
      if (!!document.querySelector('.tour-start'))
        (document.querySelector('.tour-start button') as HTMLElement).click();
      this.tourPreStart.next(true);
      this.tour.start();
    } else return false;
  }
  generateSteps(steps, instance?: TourInstance) {
    const translations = instance && this.translations[instance];

    return steps.map((step, i) => ({
      title: step.title,
      id: step.key || i.toString(),
      text: step.text,
      showOn:
        step?.showOn ||
        function () {
          return step.element instanceof HTMLElement || !!document.querySelector(step.element);
        },
      attachTo: {
        element: step.element,
        on: step?.position || 'bottom',
      },
      ...(step.scrollTo ? { scrollTo: step.scrollTo } : {}),
      popperOptions: step.popperOptions,
      buttons: [
        {
          action() {
            return this.back();
          },
          secondary: step.hasBack ?? true,
          text: this.translations.back,
        },
        {
          text: i === steps.length - 1 ? this.translations.finish : this.translations.next,
          action() {
            function action(this: any) {
              return i === steps.length - 1 ? this.complete() : this.next();
            }

            if (step.actionAfterStepComplete) return step.actionAfterStepComplete(action.bind(this));
            return action.call(this);
          },
        },
      ],
      ...(translations?.[step.key] || {}),
    }));
  }

  constructor(private translate: TranslateService) {
    this.translate
      .get('tour')
      .pipe(take(1))
      .subscribe((response: any) => (this.translations = response));
  }
}
